import { useHasAccess } from 'lib-shared-access-control';
import { useMemo } from 'react';

import { appActions } from 'app/app.actions';
import {
  CommandLauncherAction,
  useCommandLauncher,
} from 'utils/commandLauncher';
import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppNavigate } from 'utils/url/useAppNavigate';

/** Returns navigation actions for the command launcher. */
export function useNavigationActions(): readonly CommandLauncherAction[] {
  const navigate = useAppNavigate();
  const dispatch = useAppDispatch();
  const { createAction } = useCommandLauncher();

  // Feature flags
  const { knowledgeAccess } = useFeatureFlag(['knowledge-access']);

  // Access control
  const hasKnowledgeReadAccess = useHasAccess('knowledge.content:r');
  const hasKnowledgeWriteAccess = useHasAccess('knowledge.content:w');
  const hasAnalyticsAccess = useHasAccess('analytics');

  return useMemo(() => {
    // Order matches the left sidebar for familiarity
    const actions: CommandLauncherAction[] = [
      // 1. Dashboard
      createAction({
        label: 'Go to Dashboard',
        aliases: ['home', 'overview', 'start'],
        icon: 'dashboard',
        section: 'Navigation',
        performFn: () => navigate({ path: '/dashboard' }),
      }),
      // 2. Conversations
      createAction({
        label: 'Go to Conversations',
        aliases: ['inbox', 'messages', 'tickets', 'queue'],
        icon: 'conversations',
        section: 'Navigation',
        performFn: () => navigate({ path: '/conversations', params: {} }),
      }),
      // 3. Contacts
      createAction({
        label: 'Go to Contacts',
        aliases: ['find users', 'customers', 'people', 'address book'],
        icon: 'id-card',
        section: 'Navigation',
        performFn: () => navigate({ path: '/contacts/user' }),
      }),
      // 4. Search
      createAction({
        label: 'Go to Search',
        aliases: ['find conversations', 'lookup', 'search tickets'],
        icon: 'search',
        section: 'Navigation',
        performFn: () =>
          navigate({
            path: '/search/:scope',
            params: { path: { scope: 'conversations' } },
          }),
      }),
      // 5. Team
      createAction({
        label: 'Go to Team',
        aliases: ['agents', 'colleagues', 'members', 'team overview'],
        icon: 'team',
        section: 'Navigation',
        performFn: () => navigate({ path: '/team', params: { search: {} } }),
      }),
    ];

    // 6. Analytics/Intelligence - requires access
    if (hasAnalyticsAccess) {
      actions.push(
        createAction({
          label: 'Go to Intelligence',
          aliases: [
            'analytics',
            'reports',
            'stats',
            'metrics',
            'statistics',
            'data',
          ],
          icon: 'chart-bar',
          section: 'Navigation',
          performFn: () =>
            navigate({ path: '/analytics/dashboard/:slug', params: {} }),
        }),
      );
    }

    // 7. Knowledge - requires feature flag and access
    if (knowledgeAccess && hasKnowledgeWriteAccess) {
      actions.push(
        createAction({
          label: 'Go to Knowledge',
          aliases: ['kb', 'articles', 'help center', 'docs', 'knowledge base'],
          icon: 'file-text',
          section: 'Navigation',
          performFn: () => navigate({ path: '/knowledge' }),
        }),
      );
    }

    // 8. Internal Knowledge Base - requires feature flag and access
    if (knowledgeAccess && hasKnowledgeReadAccess) {
      actions.push(
        createAction({
          label: 'Go to Internal Knowledge Base',
          aliases: [
            'internal docs',
            'internal articles',
            'wiki',
            'internal kb',
          ],
          icon: 'file-text-search',
          section: 'Navigation',
          performFn: () => navigate({ path: '/internal-kb' }),
        }),
      );
    }

    // 9. Settings
    actions.push(
      createAction({
        label: 'Go to Settings',
        aliases: ['preferences', 'configuration', 'options'],
        icon: 'cog',
        section: 'Navigation',
        performFn: () => navigate({ path: '/settings/profile' }),
      }),
    );

    // Additional navigation items (not in sidebar)
    actions.push(
      createAction({
        label: 'Go to Notifications',
        aliases: ['alerts', 'updates'],
        icon: 'bell',
        section: 'Navigation',
        performFn: () => navigate({ path: '/notifications' }),
      }),
      createAction({
        label: 'Go to Drafts',
        aliases: ['unsent', 'pending'],
        icon: 'file-draft',
        section: 'Navigation',
        performFn: () => navigate({ path: '/drafts' }),
      }),
      // Keyboard shortcuts
      createAction({
        label: 'Keyboard shortcuts cheat sheet',
        aliases: ['hotkeys', 'keybindings', 'shortcuts', 'keys'],
        icon: 'keyboard',
        section: 'Navigation',
        commandName: 'app:keyboardCheatSheet',
        performFn: () => {
          dispatch(
            appActions.showModal({
              modal: { type: 'app:keyboardCheatSheet' },
            }),
          );
        },
      }),
    );

    return actions;
  }, [
    createAction,
    dispatch,
    hasAnalyticsAccess,
    hasKnowledgeReadAccess,
    hasKnowledgeWriteAccess,
    knowledgeAccess,
    navigate,
  ]);
}
